import { Chip, Grid, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import { parseSecondsToHM } from '@nx-smartmonkey/shared/helpers';
import { TimeWindows } from '@nx-smartmonkey/shared/interfaces';
import { RoutalDialog, RoutalPalette, RoutalSearchInput, RoutalTooltip } from '@nx-smartmonkey/ui';
import _ from 'lodash';
import { useEffect, useState } from 'react';

import { Client } from '../../../../domain/client/Client';
import { useSearchClients } from '../../../../hooks/client/useSearchClients';
import { useRetrieveActivePlanStops } from '../../../../hooks/plan/useRetrieveActivePlanStops';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectResources } from '../../../../hooks/project/useRetrieveActiveProjectResources';
import { useCreateStops } from '../../../../hooks/stop/useCreateStops';
import Button from '../../../../react-components/Button';
import FormattedMessage from '../../../../react-components/FormattedCustomMessage';
import Text from '../../../../react-components/Text';
import { DataTable } from '../../../../react-components/data-table/DataTable';
import eventsPanel from '../../../../services/events';

const styles = {
  gridnumservices: {
    width: `24px`,
    height: `24px`,
    backgroundColor: RoutalPalette.primary40,
    borderRadius: `12px`,
  },
  typonumservices: {
    width: `100%`,
    color: `white`,
  },
  typonumservices2: {
    color: RoutalPalette.primary40,
    paddingLeft: `8px`,
  },
  errorQuota: {
    backgroundColor: `rgba(255,130,0,0.1)`,
    color: RoutalPalette.danger,
    padding: `8px`,
    borderRadius: `16px`,
  },
};

type ServicesFromClientsDialogProps = {
  classes: {
    gridnumservices: string;
    typonumservices: string;
    typonumservices2: string;
    errorQuota: string;
  };
  open: boolean;
  handleCloseDialog: (...args: any[]) => any;
};

export const StopsFromClientsDialog = withStyles(styles)(
  ({ classes, open, handleCloseDialog }: ServicesFromClientsDialogProps) => {
    const { data: projectResources } = useRetrieveActiveProjectResources();
    const { data: activeProject } = useRetrieveActiveProject();
    const { data: planStops } = useRetrieveActivePlanStops();
    const { run: createStops } = useCreateStops();
    const [selectedClients, setSelectedClients] = useState<Client[]>([]);
    const [clientOffset, setClientOffset] = useState<number>(0);
    const [clientLimit, setClientLimit] = useState<number>(100);

    const maxStopsPerPlan = projectResources?.max_stops_plan;

    const [filterText, setFilterText] = useState<string>(``);
    const { data: clients } = useSearchClients({
      offset: clientOffset,
      limit: clientLimit,
      searchText: filterText,
    });

    const resultingQuota = () => (planStops?.length ?? 0) + selectedClients.length - (maxStopsPerPlan ?? 0);

    useEffect(() => {
      if (open) {
        setSelectedClients([]);
      }
    }, [open]);

    const { docs, limit, page, pages, total } = clients || {};

    return (
      <RoutalDialog
        open={open}
        dialogProps={{
          maxWidth: `md`,
          disableEscapeKeyDown: true,
          disableBackdropClick: true,
        }}
        onCloseDialog={handleCloseDialog}
        titleContent={<Text variant="h6" intlId="plans.dialogs.services_from_clients.title" />}
        contentStyles={{
          height: `400px`,
        }}
        footerContent={
          <Grid container>
            <Grid item sm={3} md={3} style={{ paddingLeft: `16px` }}>
              <Grid container direction="column" justifyContent="center" className={classes.gridnumservices}>
                <Typography className={classes.typonumservices} align="center">
                  {selectedClients.length}
                </Typography>
                <Typography variant="h6" className={classes.typonumservices2} align="center">
                  <FormattedMessage id="plans.servicesdialog.addservices.services" />
                </Typography>
              </Grid>
            </Grid>
            <Grid item sm={7} md={7}>
              {resultingQuota() > 0 && (
                <Grid container alignItems="center" className={classes.errorQuota}>
                  <FormattedMessage
                    id="plans.servicesdialog.addservices.quota_exceeded"
                    values={{
                      maxServices: maxStopsPerPlan,
                      surplus: resultingQuota(),
                    }}
                  />
                </Grid>
              )}
            </Grid>
            <Grid item sm={2} md={2}>
              <Grid container justifyContent="flex-end">
                <Button
                  intlId="plans.servicesdialog.create"
                  style={{ boxShadow: `none`, color: `white`, width: `200px` }}
                  disabled={resultingQuota() > 0 || selectedClients.length <= 0}
                  onClick={() => {
                    createStops({ stops: selectedClients.map((client: Client) => client.stopFromClient()) });
                    eventsPanel.createStops(`Desde Clientes`);
                    handleCloseDialog();
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        }
      >
        <Grid container direction="column" wrap="nowrap" style={{ backgroundColor: RoutalPalette.white }}>
          <Grid container item justifyContent="flex-end" style={{ height: `36px`, marginBottom: `20px` }}>
            <Grid item>
              <Grid container direction="column" justifyContent="center" style={{ height: `100%` }}>
                <RoutalSearchInput
                  inputContainerStyles={{
                    width: `260px`,
                  }}
                  onChange={_.debounce((text: any) => {
                    setFilterText(text);
                  }, 300)}
                  searchLive
                />
              </Grid>
            </Grid>
          </Grid>

          <div style={{ height: `calc(100% - 36px)` }}>
            <DataTable
              docs={(docs ?? []).map((client) => ({
                doc: client,
                checkboxIcon: { element: <div style={{ width: `24px` }} /> },
              }))}
              total={total ?? 0}
              limit={limit}
              page={page}
              pages={pages}
              emptyMessage={<Text intlId="no_results" />}
              onChangePage={({ offset, limit }) => {
                setClientOffset(offset);
                setClientLimit(limit);
              }}
              selectedIds={selectedClients.map((client) => client.id)}
              onMultipleSelection={({ selectedIds, fromGlobalCheckbox }) => {
                if (fromGlobalCheckbox) {
                  if (selectedClients.length > 0) {
                    setSelectedClients([]);
                  } else {
                    setSelectedClients(docs ?? []);
                  }
                } else {
                  const clients = docs?.filter((client) => (selectedIds ?? []).includes(client.id)) ?? [];
                  clients.push(
                    ...selectedClients.filter(
                      (selectedClient) => !(docs ?? []).map((docClient) => docClient.id).includes(selectedClient.id)
                    )
                  );
                  setSelectedClients(_.uniqBy(clients, `id`));
                }
              }}
              onRowDoubleClick={(client) => {
                const selectedClient = docs?.find((clientRow) => clientRow.id === client.id);
                if (!selectedClient) return;
                if (selectedClients.some((client) => client.id === selectedClient.id)) {
                  setSelectedClients(selectedClients.filter((client) => client.id !== selectedClient.id));
                } else {
                  setSelectedClients(_.uniqBy([...selectedClients, selectedClient], `id`));
                }
              }}
              columns={[
                {
                  accessorName: `label`,
                  name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="clients.data.details.label" />,
                  formatter: ({ value: label }) => (
                    <Grid container direction="row" style={{ height: `32px` }} alignItems="center">
                      {label && label !== `` ? (
                        <RoutalTooltip title={label}>
                          <Text variant="body">{label.length > 30 ? `${label.substring(0, 30)}...` : `${label}`}</Text>
                        </RoutalTooltip>
                      ) : (
                        <Text variant="body" style={{ color: RoutalPalette.secondary00 }} intlId="no_name" />
                      )}
                    </Grid>
                  ),
                },
                {
                  accessorName: `location`,
                  name: (
                    <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="clients.data.details.location" />
                  ),
                  formatter: ({ value: location }) =>
                    location ? (
                      <RoutalTooltip title={location?.label ?? ``}>
                        <Text variant="body" style={{ maxWidth: `240px` }}>
                          {location.label}
                        </Text>
                      </RoutalTooltip>
                    ) : (
                      `-`
                    ),
                },
                ...(activeProject?.view?.constraints?.time_windows
                  ? [
                      {
                        accessorName: `default_time_windows`,
                        name: (
                          <Text
                            style={{ color: RoutalPalette.primary20 }}
                            isBold
                            intlId="clients.data.details.default_time_windows"
                          />
                        ),
                        formatter: ({ value }: { value: TimeWindows }) =>
                          value.map((d: any) => (
                            <Chip key={d[0] + d[1]} label={`${parseSecondsToHM(d[0])}-${parseSecondsToHM(d[1])}`} />
                          )),
                      },
                    ]
                  : []),
                {
                  accessorName: `phone`,
                  name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="clients.data.details.phone" />,
                },
                {
                  accessorName: `comments`,
                  name: (
                    <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="clients.data.details.comments" />
                  ),
                  formatter: ({ value: comments }: { value: string }) => (
                    <Typography style={{ maxWidth: `700px` }} noWrap>
                      {comments}
                    </Typography>
                  ),
                },
              ]}
            />
          </div>
        </Grid>
      </RoutalDialog>
    );
  }
);
